import { useState, useCallback, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  // Generate device ID for guest users
  const [deviceId] = useState(() => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  });

  // Generate guest ID label (e.g. MusicLover_742)
  const generateGuestId = () => {
    const adjectives = ['Music', 'Beat', 'Rhythm', 'Melody', 'Harmony', 'Tune', 'Song', 'Audio'];
    const nouns = ['Lover', 'Fan', 'Master', 'Seeker', 'Hunter', 'Rider', 'Sage', 'Wizard'];
    const randomNum = Math.floor(Math.random() * 999) + 1;
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    return `${adjective}${noun}_${randomNum}`;
  };

  // API call helper
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE}/api/user-auth${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API call failed');
    }

    return data;
  };

  // Register new user
  const register = useCallback(async (userData) => {
    try {
      const response = await apiCall('/register', {
        method: 'POST',
        body: JSON.stringify({
          ...userData,
          // Don't send device_id for registered users
        }),
      });

      if (response.success) {
        // For registration, don't automatically log in - user needs to verify email
        if (response.message && response.message.includes('verify')) {
          // Registration successful but needs email verification
          return response;
        } else {
          // Login or other success - set auth state
          setUser(response.user);
          if (response.token) {
            setToken(response.token);
            localStorage.setItem('auth_token', response.token);
          }
          setIsAuthenticated(true);
        }
      }

      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }, [token]); // Removed deviceId dependency

  // Login user
  const login = useCallback(async (email, password) => {
    try {
      const response = await apiCall('/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        // No device_id needed for login
      });

      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        localStorage.setItem('auth_token', response.token);
        try {
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.removeItem('guest_user');
        } catch (_) {}
        setIsAuthenticated(true);
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [token]); // Removed deviceId dependency

  // Create guest user
  const createGuestUser = useCallback(async ({ guestName = null, profilePicture = null } = {}) => {
    try {
      const trimmedName = (guestName || '').trim();
      const guestId = generateGuestId();
      const response = await apiCall('/guest', {
        method: 'POST',
        body: JSON.stringify({
          device_id: deviceId,
          guest_name: trimmedName || 'Guest',
          guest_id_label: guestId,
          profile_picture: profilePicture,
        }),
      });

      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        try {
          localStorage.setItem('guest_user', JSON.stringify(response.user));
        } catch (_) {}
      }

      return response;
    } catch (error) {
      console.error('Create guest user error:', error);
      throw error;
    }
  }, [deviceId]);

  // Migrate guest to registered user
  const migrateGuestToRegistered = useCallback(async (userData) => {
    try {
      const response = await apiCall('/migrate-guest-by-device', {
        method: 'POST',
        body: JSON.stringify({
          ...userData,
          device_id: deviceId
        }),
      });

      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        localStorage.setItem('auth_token', response.token);
        try {
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.removeItem('guest_user');
        } catch (_) {}
        setIsAuthenticated(true);
      }

      return response;
    } catch (error) {
      console.error('Migrate guest error:', error);
      throw error;
    }
  }, [deviceId]);

  // Update user profile
  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await apiCall('/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      if (response.success) {
        setUser(response.user);
      }

      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }, [token]);

  // Get user profile
  const getProfile = useCallback(async () => {
    try {
      const response = await apiCall('/profile');
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
      }
      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }, [token]);

  // Logout
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('guest_user');
    localStorage.removeItem('user');
  }, []);

  // Apply OAuth / external auth session (e.g. Google callback)
  const applyAuthSession = useCallback((nextToken, nextUser) => {
    if (!nextToken || !nextUser) return false;
    localStorage.setItem('auth_token', nextToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    localStorage.removeItem('guest_user');
    setToken(nextToken);
    setUser(nextUser);
    setIsAuthenticated(true);
    return true;
  }, []);

  // Verify token
  const verifyToken = useCallback(async () => {
    try {
      if (!token) {
        setIsLoading(false);
        return false;
      }

      const response = await apiCall('/verify-token', {
        method: 'POST',
      });

      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        try {
          localStorage.setItem('user', JSON.stringify(response.user));
        } catch (_) {}
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token verification error:', error);
      // Token is invalid, clear it
      logout();
      return false;
    }
  }, [token, logout]);

  // Resume existing guest session (by device_id) — does not create a new guest
  const resumeGuestSession = useCallback(async () => {
    try {
      const response = await apiCall('/guest', {
        method: 'POST',
        body: JSON.stringify({ device_id: deviceId, resume: true }),
      });

      if (response.success && response.exists && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        try {
          localStorage.setItem('guest_user', JSON.stringify(response.user));
        } catch (_) {}
      }

      return response;
    } catch (error) {
      console.error('Resume guest session error:', error);
      throw error;
    }
  }, [deviceId]);

  // Guest login (alias for createGuestUser)
  const guestLogin = useCallback(async ({ guestName = null, profilePicture = null } = {}) => {
    try {
      const response = await createGuestUser({ guestName, profilePicture });
      return response;
    } catch (error) {
      console.error('Guest login error:', error);
      throw error;
    }
  }, [createGuestUser]);

  // Resend email verification
  const resendEmailVerification = useCallback(async (email) => {
    try {
      const response = await apiCall('/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  }, [token]);

  // Request password reset
  const requestPasswordReset = useCallback(async (email) => {
    try {
      const response = await apiCall('/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      return response;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (token, email, newPassword) => {
    try {
      const response = await apiCall('/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, email, newPassword }),
      });

      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      
      if (token) {
        // Optimistic hydrate from cached user (includes Google profile photo/name)
        try {
          const cachedRaw = localStorage.getItem('user');
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            if (cached && cached.user_type !== 'guest') {
              setUser(cached);
              setIsAuthenticated(true);
            }
          }
        } catch (_) {}

        await verifyToken();
      } else {
        // Rehydrate guest session if available
        try {
          const guestRaw = localStorage.getItem('guest_user');
          if (guestRaw) {
            const guest = JSON.parse(guestRaw);
            if (guest && guest.user_type === 'guest') {
              setUser(guest);
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
              setUser(null);
            }
          } else {
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (_) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, [token, verifyToken]);

  return {
    user,
    isAuthenticated,
    isLoading,
    token,
    deviceId,
    register,
    login,
    guestLogin,
    createGuestUser,
    resumeGuestSession,
    migrateGuestToRegistered,
    updateProfile,
    getProfile,
    logout,
    applyAuthSession,
    verifyToken,
    requestPasswordReset,
    resetPassword,
    resendEmailVerification,
  };
};
